import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { useAbility } from '../../context/AbilityContext.js';
import { api } from '../../services/api.js';
import { InspectionFormModal } from './InspectionFormModal.js';
import { type InspectionPhoto, PhotoLightbox } from './PhotoLightbox.js';
import {
  type PhotoWindow,
  type PhotoWindows,
  STATE_LABEL,
  STATE_TONE,
  mytWhen,
  relativeTo,
} from './photoWindows.js';

/** 307: photo links expire after 10 minutes; refresh well inside that while a booking stays open. */
const PHOTO_LINK_REFRESH_MS = 8 * 60_000;

interface InspectionFinding {
  area: string;
  /** 374: absent on a customer's guided set. */
  condition?: string;
  /** 374: `angle` or `damage` on a customer's guided set. */
  kind?: 'angle' | 'damage';
  angle?: string;
  photo_ids?: string[];
  /** 305: parallel to `photo_ids`; `url: null` for anything the platform would not vouch for. */
  photos?: InspectionPhoto[];
  /**
   * 🔴 Unrestricted callers only — `internal = can('manage','Booking')` (`bookings/routes.ts`),
   * which only the admin block grants. **For a provider this is absent on every finding**, so the
   * absence is the normal case rather than an edge one (077 research R3).
   */
  notes?: string;
}

interface Inspection {
  id: string;
  inspection_type: string;
  /** 374: absent on a customer's guided set. */
  overall_condition?: string;
  odometer_km?: number;
  fuel_level_percent?: number;
  /** Unrestricted callers only — a provider is not told who recorded it (067). */
  inspected_by?: string;
  /** 305: what kind of recorder — both audiences. Absent only on a pre-305 backend's payload. */
  recorded_as?: 'customer' | 'provider' | 'staff';
  /** 305: unrestricted callers only, and omitted when the recorder no longer exists. */
  recorded_by_name?: string;
  /** Unrestricted callers only (067). */
  notes?: string;
  /** Always published, possibly empty — unlike every other optional key (`inspectionSerializer`). */
  items: InspectionFinding[];
  created_at: string;
}

interface InspectionsResponse {
  items: Inspection[];
}

/** 305: `recorded_as` → the words staff read (data-model.md). */
const RECORDED_AS_LABEL: Record<NonNullable<Inspection['recorded_as']>, string> = {
  customer: 'Customer',
  provider: 'Host/Supplier',
  staff: 'CARii staff',
};

/** 305 (US1 AS-4): the words for the two kinds; anything unforeseen reads back as stored. */
const INSPECTION_TYPE_LABEL: Record<string, string> = {
  checkout: 'Checkout',
  checkin: 'Check-in',
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-MY');
}

/**
 * A finding's photos. A payload from before 305 carries only `photo_ids`; those are shown as
 * references with no address rather than silently dropped, so a reviewer still sees that a
 * photo was attached.
 */
function photosOf(finding: InspectionFinding): InspectionPhoto[] {
  if (finding.photos) return finding.photos;
  return (finding.photo_ids ?? []).map((id) => ({ id, url: null }));
}

/** 305: one inspection's photos; 374: or one angle's pickup and return photo side by side. */
type OpenLightbox =
  | { kind: 'inspection'; inspectionId: string; startIndex: number; title: string }
  | { kind: 'compare'; angle: string; startIndex: number; title: string };

/** 374: the angles of a guided set, in order, with the words staff read. 415: the pickup set ends with the lockbox
 * and its dashboard is the fuel photo sent after the code; the return set keeps the dashboard. */
const ANGLES: ReadonlyArray<{ angle: string; label: string }> = [
  { angle: 'front', label: 'Front' },
  { angle: 'back', label: 'Back' },
  { angle: 'left', label: 'Left side' },
  { angle: 'right', label: 'Right side' },
  { angle: 'interior', label: 'Interior' },
  { angle: 'lockbox', label: 'Lockbox' },
  { angle: 'dashboard', label: 'Dashboard' },
];

/** 374: the customer's set for a moment — the earliest customer inspection of that type. */
function customerSet(items: Inspection[], type: 'checkout' | 'checkin'): Inspection | undefined {
  return items.find((i) => i.recorded_as === 'customer' && i.inspection_type === type);
}

/** 374: a guided set carries `kind` on its findings; an older customer inspection does not. */
function isGuided(insp: Inspection | undefined): boolean {
  return Boolean(insp?.items.some((f) => f.kind));
}

function anglePhoto(insp: Inspection | undefined, angle: string): InspectionPhoto | undefined {
  const finding = insp?.items.find((f) => f.kind === 'angle' && f.angle === angle);
  return finding ? photosOf(finding)[0] : undefined;
}

/**
 * The booking's Inspections card — extracted from `BookingDetailPage` by 305, which added the
 * photographs. Moved as-is first (query key, record buttons, both empty-state sentences and their
 * reasons), so `BookingDetailPage.test.tsx`'s pins keep meaning what they meant.
 */
export function InspectionsSection({
  bookingId,
  onCheckinSaved,
  photoWindows,
  pickupAt,
  returnAt,
}: {
  bookingId: string;
  /** 311 (#549, US4): a CHECK-IN inspection was saved — the car is back. Never fired for checkout. */
  onCheckinSaved?: () => void;
  /** 374: the customer's photo windows for this rental, as the booking published them. */
  photoWindows?: PhotoWindows;
  /** 374: the rental's first pickup and last return, for "6 min after pickup". */
  pickupAt?: string;
  returnAt?: string;
}) {
  const ability = useAbility();
  // 305 (FR-005): only the unrestricted audience is ever sent a recorder's name — the same
  // derivation the backend uses for `internal` — so only they are told when one is missing.
  const seesRecorderName = ability.can('manage', 'Booking');
  const [inspectionType, setInspectionType] = useState<'checkout' | 'checkin' | null>(null);
  const [lightbox, setLightbox] = useState<OpenLightbox | null>(null);
  const [view, setView] = useState<'compare' | 'timeline' | null>(null);
  // 374: opened from the list's Photos tag (`#car-condition`), the page lands on this card.
  useEffect(() => {
    if (window.location.hash !== '#car-condition') return;
    document.getElementById('car-condition')?.scrollIntoView?.({ block: 'start' });
  }, []);
  const [brokenIds, setBrokenIds] = useState<Set<string>>(new Set());
  // The thumbnail that opened the lightbox, so closing it hands focus back (US1 AS-2) — without
  // this a keyboard user is dropped at the top of the page after every photo they inspect.
  const openerRef = useRef<HTMLButtonElement | null>(null);

  // The key `InspectionFormModal` invalidates on success — the two must match, or a
  // just-recorded inspection stays invisible until a full reload (H13).
  // 307 (FR-006): photo links are signed and expire after 10 minutes, so an open page must keep
  // them fresh — on returning to the tab, and every 8 minutes while it stays open.
  const { data: inspectionsData, refetch } = useQuery<InspectionsResponse>({
    queryKey: ['booking-inspections', bookingId],
    queryFn: () => api.get(`/bookings/${bookingId}/inspections`),
    refetchOnWindowFocus: true,
    refetchInterval: PHOTO_LINK_REFRESH_MS,
  });

  // 307: a photo that fails to load most likely holds an expired link — fetch fresh links ONCE; if it
  // fails again it is genuinely unavailable. Tracked per photo so a broken file cannot loop refetches.
  const retriedIds = useRef<Set<string>>(new Set());
  function handlePhotoError(photoId: string) {
    if (!retriedIds.current.has(photoId)) {
      retriedIds.current.add(photoId);
      void refetch();
      return;
    }
    setBrokenIds((prev) => new Set(prev).add(photoId));
  }

  const all = inspectionsData?.items ?? [];
  const pickupSet = customerSet(all, 'checkout');
  const returnSet = customerSet(all, 'checkin');
  const guided = isGuided(pickupSet) || isGuided(returnSet);
  // 374: Compare when a guided customer set exists, else the Timeline every booking had before.
  const shown = view ?? (guided ? 'compare' : 'timeline');

  // The open lightbox reads the CURRENT photos, so a refetch's fresh links reach it too.
  let lightboxPhotos: InspectionPhoto[] = [];
  if (lightbox?.kind === 'inspection') {
    lightboxPhotos = (all.find((i) => i.id === lightbox.inspectionId)?.items ?? []).flatMap((f) =>
      photosOf(f).map((p) => ({ ...p, label: f.area })),
    );
  } else if (lightbox?.kind === 'compare') {
    const label = ANGLES.find((a) => a.angle === lightbox.angle)?.label ?? lightbox.angle;
    lightboxPhotos = [
      ...(anglePhoto(pickupSet, lightbox.angle)
        ? [
            {
              ...(anglePhoto(pickupSet, lightbox.angle) as InspectionPhoto),
              label: `${label} — pickup`,
            },
          ]
        : []),
      ...(anglePhoto(returnSet, lightbox.angle)
        ? [
            {
              ...(anglePhoto(returnSet, lightbox.angle) as InspectionPhoto),
              label: `${label} — return`,
            },
          ]
        : []),
    ];
  }

  function closeLightbox() {
    setLightbox(null);
    openerRef.current?.focus();
  }

  return (
    <>
      <Card data-testid="inspections-section" id="car-condition" data-jump="Car condition">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h2 className="text-sm font-semibold text-steel uppercase tracking-wide">
            Car condition
          </h2>
          {all.length > 0 && (
            <fieldset className="ml-auto inline-flex overflow-hidden rounded-md border border-hairline text-xs font-semibold">
              <legend className="sr-only">Car condition view</legend>
              {(['compare', 'timeline'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  aria-pressed={shown === v}
                  onClick={() => setView(v)}
                  className={`px-3 py-1 ${shown === v ? 'bg-brand-blue-soft text-primary-deep' : 'text-steel'}`}
                >
                  {v === 'compare' ? 'Compare' : 'Timeline'}
                </button>
              ))}
            </fieldset>
          )}
          {/* 063's principle: gate on the action the destination performs. A gate on `manage`
              would hide this from the very providers H12 exists to admit. */}
          {ability.can('create', 'BookingInspection') && (
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={() => setInspectionType('checkout')}>
                Record checkout inspection
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setInspectionType('checkin')}>
                Record check-in inspection
              </Button>
            </div>
          )}
        </div>

        {photoWindows && (
          <CustomerMoments
            windows={photoWindows}
            pickupSet={pickupSet}
            returnSet={returnSet}
            pickupAt={pickupAt}
            returnAt={returnAt}
            seesRecorderName={seesRecorderName}
          />
        )}

        {shown === 'compare' && all.length > 0 ? (
          <CompareView
            pickupSet={pickupSet}
            returnSet={returnSet}
            staff={all.filter((i) => i.recorded_as !== 'customer')}
            brokenIds={brokenIds}
            onPhotoError={handlePhotoError}
            onOpen={(angle, startIndex, title, opener) => {
              openerRef.current = opener;
              setLightbox({ kind: 'compare', angle, startIndex, title });
            }}
            onOpenInspection={(inspectionId, startIndex, title, opener) => {
              openerRef.current = opener;
              setLightbox({ kind: 'inspection', inspectionId, startIndex, title });
            }}
            seesRecorderName={seesRecorderName}
          />
        ) : inspectionsData?.items.length ? (
          <ul className="space-y-2">
            {inspectionsData.items.map((insp) => {
              // Photos are numbered across THIS inspection, in finding order — the set the lightbox
              // steps through (`lightboxPhotos`). Another inspection's photos never join it (US1 AS-4).
              let offset = 0;
              return (
                <li key={insp.id} className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                  <span data-testid="inspection-type" className="font-medium">
                    {INSPECTION_TYPE_LABEL[insp.inspection_type] ?? insp.inspection_type}
                  </span>
                  {insp.overall_condition && (
                    <span className="text-steel capitalize">{insp.overall_condition}</span>
                  )}
                  {insp.recorded_as && (
                    <span
                      data-testid="inspection-recorded-as"
                      className="rounded-full bg-surface-feature px-2 py-0.5 text-xs font-medium text-ink"
                    >
                      {RECORDED_AS_LABEL[insp.recorded_as]}
                    </span>
                  )}
                  {insp.recorded_as && seesRecorderName && (
                    <span data-testid="inspection-recorded-by" className="text-steel">
                      {insp.recorded_by_name ?? 'Name unavailable'}
                    </span>
                  )}
                  {insp.odometer_km !== undefined && (
                    <span className="text-steel">{insp.odometer_km} km</span>
                  )}
                  {insp.fuel_level_percent !== undefined && (
                    <span className="text-steel">{insp.fuel_level_percent}% fuel</span>
                  )}
                  <span className="text-muted text-xs">{formatDate(insp.created_at)}</span>
                  {insp.notes && <p className="w-full text-steel mt-0.5">{insp.notes}</p>}
                  {insp.items.length === 0 && (
                    // Words, not an empty gap — and a DIFFERENT sentence from "No inspections
                    // recorded for this booking.", because an inspection that found nothing and a
                    // booking nobody inspected are different facts (FR-012).
                    <p data-testid="inspection-no-findings" className="w-full text-muted text-xs">
                      No areas were recorded on this inspection.
                    </p>
                  )}
                  <ul className="w-full mt-1 space-y-1">
                    {insp.items.map((finding) => {
                      const photos = photosOf(finding);
                      const start = offset;
                      offset += photos.length;
                      return (
                        <li
                          key={`${insp.id}-${finding.angle ?? finding.area}-${finding.condition ?? finding.kind}`}
                          data-testid="inspection-finding"
                          className="flex flex-wrap gap-x-3 text-sm border-l-2 border-hairline pl-2"
                        >
                          {/* No `capitalize` here, unlike the condition beside it: an area may
                              have been typed by hand (US3), and it must read back exactly as typed
                              — a display that title-cases it makes the panel disagree with the
                              record about what the part is called (mutation M13). */}
                          <span data-testid="finding-area" className="font-medium">
                            {finding.area}
                          </span>
                          {finding.condition && (
                            <span className="text-steel capitalize">{finding.condition}</span>
                          )}
                          {/* Guarded because for a provider it is absent on EVERY finding, not on
                              an unusual one (research R3) — `notes` is withheld from anyone without
                              `manage Booking`. Mutation M9 covers the unguarded version. */}
                          {finding.notes && (
                            <span className="w-full text-muted text-xs">{finding.notes}</span>
                          )}
                          {/* 305: divs, not a list — the page's tests count `listitem`s as rows. */}
                          <div className="w-full mt-1 flex flex-wrap gap-2">
                            {photos.length === 0 ? (
                              <span data-testid="finding-no-photo" className="text-muted text-xs">
                                No photo
                              </span>
                            ) : (
                              photos.map((photo, i) =>
                                photo.url && !brokenIds.has(photo.id) ? (
                                  <button
                                    key={photo.id}
                                    type="button"
                                    aria-label={`Open photo ${i + 1} of ${finding.area}`}
                                    className="h-20 w-20 overflow-hidden rounded-lg border border-hairline focus:outline-none focus:ring-2 focus:ring-primary"
                                    onClick={(e) => {
                                      openerRef.current = e.currentTarget;
                                      setLightbox({
                                        kind: 'inspection',
                                        inspectionId: insp.id,
                                        startIndex: start + i,
                                        title: finding.area,
                                      });
                                    }}
                                  >
                                    <img
                                      data-testid="inspection-thumbnail"
                                      src={photo.url}
                                      alt={`${finding.area}, ${i + 1} of ${photos.length}`}
                                      loading="lazy"
                                      decoding="async"
                                      className="h-full w-full object-cover"
                                      onError={() => handlePhotoError(photo.id)}
                                    />
                                  </button>
                                ) : (
                                  <span
                                    key={photo.id}
                                    data-testid="inspection-photo-unavailable"
                                    className="flex h-20 w-20 items-center justify-center rounded-lg border border-dashed border-hairline bg-surface p-1 text-center text-xs text-muted"
                                  >
                                    Photo unavailable
                                  </span>
                                ),
                              )
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              );
            })}
          </ul>
        ) : (
          // Words, not an empty box: a section rendering nothing cannot be told apart from one
          // that failed to load.
          <p data-testid="inspections-empty" className="text-sm text-muted">
            No inspections recorded for this booking.
          </p>
        )}
      </Card>

      {lightbox && (
        <PhotoLightbox
          key={`${lightbox.kind === 'inspection' ? lightbox.inspectionId : lightbox.angle}-${lightbox.startIndex}`}
          photos={lightboxPhotos}
          onPhotoError={handlePhotoError}
          isPhotoBroken={(id) => brokenIds.has(id)}
          startIndex={lightbox.startIndex}
          title={lightbox.title}
          onClose={closeLightbox}
        />
      )}

      {inspectionType && (
        <InspectionFormModal
          bookingId={bookingId}
          inspectionType={inspectionType}
          onClose={() => setInspectionType(null)}
          onSaved={inspectionType === 'checkin' ? onCheckinSaved : undefined}
        />
      )}
    </>
  );
}

const RECORDED_AS_WORD = RECORDED_AS_LABEL;

/** 374: one moment's box — its state, who sent it and when relative to the moment, how many photos. */
function MomentBox({
  title,
  word,
  w,
  set,
  momentAt,
  seesRecorderName,
}: {
  title: string;
  word: 'pickup' | 'return';
  w: PhotoWindow;
  set: Inspection | undefined;
  momentAt?: string;
  seesRecorderName: boolean;
}) {
  let line: string;
  if (w.state === 'sent') {
    const at = w.sent_at ?? set?.created_at;
    const parts = [
      seesRecorderName && set?.recorded_by_name ? set.recorded_by_name : null,
      at ? mytWhen(at) : null,
      at && momentAt ? relativeTo(at, momentAt, word) : null,
      w.photo_count !== undefined
        ? `${w.photo_count} ${w.photo_count === 1 ? 'photo' : 'photos'}`
        : null,
    ].filter(Boolean);
    line = parts.join(' · ');
  } else if (w.state === 'open') {
    line = `Customer can send until ${mytWhen(w.closes_at)}.`;
  } else if (w.state === 'later') {
    line = `Opens ${mytWhen(w.opens_at)}.`;
  } else if (w.state === 'missed') {
    line = `Window closed ${mytWhen(w.closes_at)}. The customer took no photos.`;
  } else {
    line = 'The rental is not paid, or was cancelled.';
  }
  return (
    <div
      data-testid={`moment-${word}`}
      className="flex flex-col gap-1 rounded-lg border border-hairline p-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-ink">{title}</span>
        <span
          data-testid={`moment-${word}-state`}
          className={`rounded-full px-2 py-0.5 text-xs font-bold ${STATE_TONE[w.state]}`}
        >
          {STATE_LABEL[w.state]}
        </span>
        {w.state === 'sent' && (
          <span className="rounded-full bg-surface-feature px-2 py-0.5 text-xs font-medium text-ink">
            {RECORDED_AS_WORD.customer}
          </span>
        )}
        {word === 'return' && (w.damage_count ?? 0) > 0 && (
          <span
            data-testid="moment-return-damage"
            className="rounded-full bg-semantic-danger-bg px-2 py-0.5 text-xs font-bold text-semantic-danger-text"
          >
            Damage reported at return
          </span>
        )}
      </div>
      <p data-testid={`moment-${word}-line`} className="text-xs text-steel">
        {line}
      </p>
    </div>
  );
}

function CustomerMoments({
  windows,
  pickupSet,
  returnSet,
  pickupAt,
  returnAt,
  seesRecorderName,
}: {
  windows: PhotoWindows;
  pickupSet?: Inspection;
  returnSet?: Inspection;
  pickupAt?: string;
  returnAt?: string;
  seesRecorderName: boolean;
}) {
  return (
    <div className="mb-3 flex flex-col gap-2">
      <div className="grid gap-2 sm:grid-cols-2">
        <MomentBox
          title="Pickup"
          word="pickup"
          w={windows.pickup}
          set={pickupSet}
          momentAt={pickupAt}
          seesRecorderName={seesRecorderName}
        />
        <MomentBox
          title="Return"
          word="return"
          w={windows.return}
          set={returnSet}
          momentAt={returnAt}
          seesRecorderName={seesRecorderName}
        />
      </div>
      {windows.fuel &&
        (windows.fuel.state === 'sent' ||
          windows.fuel.state === 'open' ||
          windows.fuel.state === 'missed') && <FuelLine w={windows.fuel} />}
      {windows.pickup.state === 'missed' && (
        <p
          data-testid="no-pickup-photos"
          className="rounded-md bg-semantic-warning-bg px-3 py-2 text-sm text-semantic-warning-text"
        >
          No pickup photos: any damage found at return cannot be compared with the car's state at
          pickup.
        </p>
      )}
    </div>
  );
}

/** 415 (#1520): the fuel photo, asked after the code and never required, so one line rather than a box. */
function FuelLine({ w }: { w: PhotoWindow }) {
  const line =
    w.state === 'sent'
      ? `Fuel photo · ${w.sent_at ? mytWhen(w.sent_at) : 'sent'}`
      : w.state === 'open'
        ? `Fuel photo · Customer can send until ${mytWhen(w.closes_at)}.`
        : 'Fuel photo not sent';
  return (
    <p data-testid="moment-fuel" className="flex flex-wrap items-center gap-2 text-sm text-ink">
      <span
        data-testid="moment-fuel-state"
        className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATE_TONE[w.state]}`}
      >
        {STATE_LABEL[w.state]}
      </span>
      {line}
    </p>
  );
}

function Thumb({
  photo,
  label,
  broken,
  onError,
  onOpen,
}: {
  photo: InspectionPhoto | undefined;
  label: string;
  broken: boolean;
  onError: (id: string) => void;
  onOpen: (opener: HTMLButtonElement) => void;
}) {
  if (!photo) {
    return (
      <span className="flex aspect-[4/3] items-center justify-center rounded-lg border border-dashed border-hairline bg-surface text-xs text-muted">
        No photo
      </span>
    );
  }
  if (!photo.url || broken) {
    return (
      <span
        data-testid="inspection-photo-unavailable"
        className="flex aspect-[4/3] items-center justify-center rounded-lg border border-dashed border-hairline bg-surface text-xs text-muted"
      >
        Photo unavailable
      </span>
    );
  }
  return (
    <button
      type="button"
      aria-label={`Open ${label}`}
      className="aspect-[4/3] overflow-hidden rounded-lg border border-hairline focus:outline-none focus:ring-2 focus:ring-primary"
      onClick={(e) => onOpen(e.currentTarget)}
    >
      <img
        src={photo.url}
        alt={label}
        loading="lazy"
        decoding="async"
        className="h-full w-full object-cover"
        onError={() => onError(photo.id)}
      />
    </button>
  );
}

/** 374 (US5): each angle's pickup and return photo side by side, damage reported, the customer's notes, staff inspections. */
function CompareView({
  pickupSet,
  returnSet,
  staff,
  brokenIds,
  onPhotoError,
  onOpen,
  onOpenInspection,
  seesRecorderName,
}: {
  pickupSet?: Inspection;
  returnSet?: Inspection;
  staff: Inspection[];
  brokenIds: Set<string>;
  onPhotoError: (id: string) => void;
  onOpen: (angle: string, startIndex: number, title: string, opener: HTMLButtonElement) => void;
  onOpenInspection: (
    id: string,
    startIndex: number,
    title: string,
    opener: HTMLButtonElement,
  ) => void;
  seesRecorderName: boolean;
}) {
  const damage = [
    ...(pickupSet?.items ?? [])
      .filter((f) => f.kind === 'damage')
      .map((f) => ({ f, at: 'pickup' as const })),
    ...(returnSet?.items ?? [])
      .filter((f) => f.kind === 'damage')
      .map((f) => ({ f, at: 'return' as const })),
  ];
  const notes = [
    pickupSet?.notes ? { at: 'pickup', text: pickupSet.notes } : null,
    returnSet?.notes ? { at: 'return', text: returnSet.notes } : null,
  ].filter((n): n is { at: string; text: string } => n !== null);
  return (
    <div data-testid="compare-view" className="flex flex-col gap-4">
      <div className="grid grid-cols-[minmax(72px,110px)_minmax(0,220px)_minmax(0,220px)] items-center gap-2">
        <span />
        <span className="text-xs font-bold text-steel">Pickup</span>
        <span className="text-xs font-bold text-steel">Return</span>
        {ANGLES.map(({ angle, label }) => {
          const p = anglePhoto(pickupSet, angle);
          const r = anglePhoto(returnSet, angle);
          return (
            <div key={angle} data-testid={`compare-${angle}`} className="contents">
              <span className="text-sm font-semibold text-ink">{label}</span>
              <Thumb
                photo={p}
                label={`${label} at pickup`}
                broken={p ? brokenIds.has(p.id) : false}
                onError={onPhotoError}
                onOpen={(el) => onOpen(angle, 0, label, el)}
              />
              <Thumb
                photo={r}
                label={`${label} at return`}
                broken={r ? brokenIds.has(r.id) : false}
                onError={onPhotoError}
                onOpen={(el) => onOpen(angle, p ? 1 : 0, label, el)}
              />
            </div>
          );
        })}
      </div>

      {damage.length > 0 && (
        <div>
          <p className="mb-1.5 text-sm font-semibold text-ink">Damage reported</p>
          <ul className="grid gap-2 sm:grid-cols-2">
            {damage.map(({ f, at }, i) => {
              const photo = photosOf(f)[0];
              const set = at === 'pickup' ? pickupSet : returnSet;
              const index = (set?.items ?? [])
                .flatMap((x) => photosOf(x))
                .findIndex((x) => x.id === photo?.id);
              return (
                <li
                  key={`${at}-${photo?.id ?? i}`}
                  data-testid="damage-reported"
                  className="flex gap-2.5 rounded-lg border border-semantic-danger-bg p-2.5"
                >
                  <div className="w-24 flex-none">
                    <Thumb
                      photo={photo}
                      label={f.area}
                      broken={photo ? brokenIds.has(photo.id) : false}
                      onError={onPhotoError}
                      onOpen={(el) =>
                        set && onOpenInspection(set.id, Math.max(index, 0), f.area, el)
                      }
                    />
                  </div>
                  <div className="text-sm">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-bold ${
                        at === 'return'
                          ? 'bg-semantic-danger-bg text-semantic-danger-text'
                          : 'bg-surface-feature text-ink'
                      }`}
                    >
                      {at === 'return' ? 'Reported at return' : 'At pickup'}
                    </span>
                    <p className="mt-1 text-ink">{f.area}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {notes.map((n) => (
        <p
          key={n.at}
          data-testid={`customer-note-${n.at}`}
          className="rounded-md bg-surface px-3 py-2 text-sm text-ink"
        >
          <span className="font-semibold">Customer's note at {n.at}:</span> {n.text}
        </p>
      ))}

      <div className="flex flex-col gap-1.5 border-t border-hairline pt-3">
        <p className="text-sm font-semibold text-ink">Staff inspections</p>
        {staff.length === 0 ? (
          <p data-testid="no-staff-inspections" className="text-sm text-muted">
            No staff inspection recorded.
          </p>
        ) : (
          staff.map((insp) => {
            const photos = insp.items.flatMap((f) => photosOf(f));
            return (
              <div
                key={insp.id}
                data-testid="staff-inspection"
                className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm"
              >
                <span className="rounded-full bg-surface-feature px-2 py-0.5 text-xs font-medium text-ink">
                  {insp.recorded_as ? RECORDED_AS_LABEL[insp.recorded_as] : 'Staff'}
                </span>
                <span className="font-medium">
                  {INSPECTION_TYPE_LABEL[insp.inspection_type] ?? insp.inspection_type}
                </span>
                {seesRecorderName && insp.recorded_by_name && (
                  <span className="text-steel">{insp.recorded_by_name}</span>
                )}
                <span className="text-steel">{mytWhen(insp.created_at)}</span>
                {insp.odometer_km !== undefined && (
                  <span className="text-steel">{insp.odometer_km} km</span>
                )}
                {insp.fuel_level_percent !== undefined && (
                  <span className="text-steel">{insp.fuel_level_percent}% fuel</span>
                )}
                <span className="text-steel">
                  {photos.length} {photos.length === 1 ? 'photo' : 'photos'}
                </span>
                {photos.length > 0 && (
                  <button
                    type="button"
                    className="text-primary text-xs underline"
                    onClick={(e) =>
                      onOpenInspection(
                        insp.id,
                        0,
                        INSPECTION_TYPE_LABEL[insp.inspection_type] ?? 'Inspection',
                        e.currentTarget,
                      )
                    }
                  >
                    View photos
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
